import { ScenarioExt } from '@/classes/scenario/ScenarioExt';
import { IRule } from '@/classes/rules/IRule';
import { INestedRule } from '@/classes/rules/INestedRule';
import { DefaultValue } from '@/classes/rules/DefaultValue';
import { RuleFieldMessage } from '@/classes/rules/RuleFieldMessage';
import { Invalidation } from '@/classes/rules/Invalidation';
import { FieldUsage } from '@/classes/rules/FieldUsage';
import { ObjectOnly } from '@/classes/ObjectOnly';

type Dictionary<Value> = Record<string, Value>
const noDefaultValue = () => null;
const available = () => true;
const noFalsyValues: <ValueType extends ObjectOnly>(val: ValueType, errorMessages?: RuleFieldMessage[]) => boolean = val => !!val;

/**
 * Generic class implemented INestedRule.
 * The type T specifies the type of object this rule applies to.
 * It can contain rules to handle each property of this object,
 * this rules can ben themselves ObjectRules
 */
export class ObjectRule<ValueType extends ObjectOnly, Context = ScenarioExt> implements INestedRule<Context> {
  public key: string;
  public required: boolean = true;
  public displayed: boolean = true;
  public rules: Dictionary<IRule<Context>> = {};
  private errorMessages: RuleFieldMessage[] = [];
  public fieldUsage: FieldUsage;
  public getDefaultInstance: (context?: Context) => DefaultValue<ValueType> | null;
  public isAvailable: (context: Context) => boolean;
  private checkFunction: (val: ValueType, errorMessages?: RuleFieldMessage[]) => boolean;

  /**
   * Rule constructor.
   * @param key rule key
   * @param rules rules that will be nested
   * @param isAvailable availability algorithm of the field
   * @param customCheck customized check of the object value
   * @param getDefaultInstance default value algorithm - check how it works
   * @param required is the field required or not
   */
  public constructor(
    key: string,
    rules?: IRule<Context>[],
    isAvailable?: (context: Context) => boolean,
    customCheck?: (val: ValueType) => boolean,
    getDefaultInstance?: (context?: Context) => DefaultValue<ValueType> | null,
    required?: boolean
  ) {
    this.key = key;
    this.checkFunction = customCheck ?? noFalsyValues;
    this.isAvailable = isAvailable ?? available;
    this.getDefaultInstance = getDefaultInstance ?? noDefaultValue;
    if (rules) {
      for (const rule of rules) {
        this.rules[rule.key] = rule;
      }
    }
    if (required === false || required === true) {
      this.required = required;
    }
    this.fieldUsage = new FieldUsage();
  }

  public get invalidation(): Invalidation {
    return this.fieldUsage.getInvalidation();
  }

  public checkValue(val: ValueType | null, context: Context): boolean | null {
    this.errorMessages = [];
    if (val === null) {
      return null;
    }
    return this.checkFunction(val, this.errorMessages);
  }

  public getNestedRule(key: string): IRule<Context> | undefined {
    return this.rules[key];
  }

  public getErrorMessages(): RuleFieldMessage[] {
    return Array.from(this.errorMessages);
  }

  /**
   * Computes the field default value with its own default value,
   * but with overrides of the contained rules default values.
   * @param context context to computed the default value
   */
  public getDefaultValue(context?: Context): DefaultValue<ValueType> | null {
    // We get the instance base with getDefaultInstance
    const defaultValue = this.getDefaultInstance(context);
    const instanceValue = defaultValue && defaultValue.value;
    // We check it's an ok object, if it's not we return null.
    if (!(instanceValue instanceof Object)) {
      return null;
    }
    // Now we know it's an object, for each nestedRule, if it has the correct key,
    // we set the base[rule.key] property with the corresponding default value.
    // So it's recursive on nested ObjectRules, as long as there is a getDefaultInstance which doesn't return null.
    for (const ruleAndKey of Object.entries(this.rules)) {
      const key = ruleAndKey[0];
      const rule = ruleAndKey[1];
      // TODO : IRule will have getDefaultValue later, so we won't have to cast rule as any
      if (Object.prototype.hasOwnProperty.call(instanceValue, key) && (rule as any).getDefaultValue) {
        const fieldDefaultValue = (rule as any).getDefaultValue(context);
        if (fieldDefaultValue instanceof DefaultValue) {
          (instanceValue as any)[key] = fieldDefaultValue.value;
        }
      }
    }
    // Then we return the defaultValue.
    return defaultValue;
  }

  // 'builder-syntax' methods
  withIsAvailable(isAvailable: (context: Context) => boolean): this {
    this.isAvailable = isAvailable;
    return this;
  }

  public withCustomCheck(customCheck: (val: ValueType, errorMessages?: RuleFieldMessage[]) => boolean): this {
    this.checkFunction = customCheck;
    return this;
  }

  public withDefaultValue(getDefaultInstance: (context?: Context) => DefaultValue<ValueType> | null): this {
    this.getDefaultInstance = getDefaultInstance;
    return this;
  }

  public withRequired(isRequired?: boolean): this { // true is default setting for this function
    this.required = isRequired !== false;
    return this;
  }

  public withFieldUsage(fieldUsage: FieldUsage): this {
    this.fieldUsage = fieldUsage;
    return this;
  }
}
